<script setup>
import { computed, onUnmounted, ref } from 'vue';
import { useToggle } from '@vueuse/core';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import EmailTranscriptModal from './EmailTranscriptModal.vue';
import ResolveAction from '../../buttons/ResolveAction.vue';
import {
  fetchAllMessages,
  openPdfWindow,
  renderConversationPdf,
} from './useConversationPdf';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ConversationApi from 'dashboard/api/inbox/conversation';
import { toast } from 'dashboard/helper/toast';

import {
  CMD_MUTE_CONVERSATION,
  CMD_SEND_TRANSCRIPT,
  CMD_UNMUTE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

defineProps({
  // Esconde o dropdown "..." (Bloquear/Transcrição/Exportar PDF) mantendo
  // o ResolveAction visível. Usado em overlays como o kanban Sheet.
  hideExtraActions: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();
const { t } = useI18n();

const [showEmailActionsModal, toggleEmailModal] = useToggle(false);
const [showActionsDropdown, toggleDropdown] = useToggle(false);

const currentChat = computed(() => store.getters.getSelectedChat);

const actionMenuItems = computed(() => {
  const items = [];

  if (!currentChat.value.muted) {
    items.push({
      icon: 'i-lucide-volume-off',
      label: t('CONTACT_PANEL.MUTE_CONTACT'),
      action: 'mute',
      value: 'mute',
    });
  } else {
    items.push({
      icon: 'i-lucide-volume-1',
      label: t('CONTACT_PANEL.UNMUTE_CONTACT'),
      action: 'unmute',
      value: 'unmute',
    });
  }

  items.push({
    icon: 'i-lucide-share',
    label: t('CONTACT_PANEL.SEND_TRANSCRIPT'),
    action: 'send_transcript',
    value: 'send_transcript',
  });

  items.push({
    icon: 'i-lucide-file-down',
    label: t('CONTACT_PANEL.EXPORT_PDF'),
    action: 'export_pdf',
    value: 'export_pdf',
  });

  // Webchat de continuidade: encerrar o modo web de propósito. A próxima
  // mensagem vai pra Meta (cobrada) — por isso passa por confirmação.
  if (currentChat.value.webchat) {
    items.push({
      icon: 'i-lucide-message-circle-off',
      label: t('CONVERSATION.WEBCHAT.CLOSE_ACTION'),
      action: 'webchat_close',
      value: 'webchat_close',
    });
  }

  return items;
});

const webchatCloseDialog = ref(null);
const isClosingWebchat = ref(false);

const confirmWebchatClose = async () => {
  if (isClosingWebchat.value) return;
  isClosingWebchat.value = true;
  // toast.promise do vue-sonner devolve o id do toast, não a promise: com
  // `await` nele o diálogo fechava e o botão destravava com o POST ainda em
  // voo — o 2º clique mandava outro webchat_close e o atendente via 404 de
  // uma ação que deu certo. Espera-se a MESMA promise à parte.
  const request = ConversationApi.webchatClose(currentChat.value.id);
  toast.promise(request, {
    loading: t('CONVERSATION.WEBCHAT.CLOSING'),
    success: t('CONVERSATION.WEBCHAT.CLOSED'),
    error: error =>
      t('CONVERSATION.WEBCHAT.CLOSE_ERROR', {
        error: error?.response?.data?.error || error?.message || String(error),
      }),
  });
  try {
    await request;
  } catch {
    // Já divulgado pelo toast de erro acima.
  } finally {
    isClosingWebchat.value = false;
    webchatCloseDialog.value?.close();
  }
};

const exportPdf = async () => {
  const conversation = currentChat.value;
  const win = openPdfWindow();
  if (!win) {
    toast.error(t('CONTACT_PANEL.EXPORT_PDF_POPUP_BLOCKED'));
    return;
  }
  const inbox = store.getters['inboxes/getInbox'](conversation.inbox_id);
  // Mesma regra do webchat_close: toast.promise não devolve a promise.
  const request = fetchAllMessages(conversation.id);
  toast.promise(request, {
    loading: t('CONTACT_PANEL.EXPORT_PDF_LOADING'),
    success: t('CONTACT_PANEL.EXPORT_PDF_READY'),
    error: error =>
      t('CONTACT_PANEL.EXPORT_PDF_ERROR', {
        error: error?.response?.data?.error || error?.message || String(error),
      }),
  });
  try {
    const messages = await request;
    renderConversationPdf(win, conversation, messages, inbox?.name || '');
  } catch {
    // Já divulgado pelo toast de erro acima; a janela de carregamento sai.
    win.close();
  }
};

const handleActionClick = ({ action }) => {
  toggleDropdown(false);

  if (action === 'mute') {
    store.dispatch('muteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
  } else if (action === 'unmute') {
    store.dispatch('unmuteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
  } else if (action === 'send_transcript') {
    toggleEmailModal();
  } else if (action === 'export_pdf') {
    exportPdf();
  } else if (action === 'webchat_close') {
    webchatCloseDialog.value?.open();
  }
};

// These functions are needed for the event listeners
const mute = () => {
  store.dispatch('muteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
};

const unmute = () => {
  store.dispatch('unmuteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
};

emitter.on(CMD_MUTE_CONVERSATION, mute);
emitter.on(CMD_UNMUTE_CONVERSATION, unmute);
emitter.on(CMD_SEND_TRANSCRIPT, toggleEmailModal);

onUnmounted(() => {
  emitter.off(CMD_MUTE_CONVERSATION, mute);
  emitter.off(CMD_UNMUTE_CONVERSATION, unmute);
  emitter.off(CMD_SEND_TRANSCRIPT, toggleEmailModal);
});
</script>

<template>
  <div class="relative flex items-center gap-2 actions--container">
    <ResolveAction
      :conversation-id="currentChat.id"
      :status="currentChat.status"
    />
    <div
      v-if="!hideExtraActions"
      v-on-clickaway="() => toggleDropdown(false)"
      class="relative flex items-center group"
    >
      <ButtonV4
        v-tooltip="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-more-vertical"
        class="rounded-md group-hover:bg-n-alpha-2"
        @click="toggleDropdown()"
      />
      <DropdownMenu
        v-if="showActionsDropdown"
        :menu-items="actionMenuItems"
        class="mt-1 ltr:right-0 rtl:left-0 top-full"
        @action="handleActionClick"
      />
    </div>
    <EmailTranscriptModal
      v-if="showEmailActionsModal"
      :show="showEmailActionsModal"
      :current-chat="currentChat"
      @cancel="toggleEmailModal"
    />
  </div>
  <Dialog
    ref="webchatCloseDialog"
    type="alert"
    :title="t('CONVERSATION.WEBCHAT.CLOSE_TITLE')"
    :description="t('CONVERSATION.WEBCHAT.CLOSE_DESCRIPTION')"
    :confirm-button-label="t('CONVERSATION.WEBCHAT.CLOSE_CONFIRM')"
    :is-loading="isClosingWebchat"
    @confirm="confirmWebchatClose"
  />
</template>
